/**
 * Plan storage — cache + IO helpers for Snowflake-saved plans.
 *
 * Mirrors the pattern in `../estimate-uploads/storage.ts`:
 *   - One in-memory cache (Map<planId, SavedPlan>)
 *   - Hydrate once at MediaPlanBuilder mount via GET /snowflake/plans
 *   - Sync read helpers (listSavedPlans / getSavedPlanById / ...) after hydrate
 *   - Async writes (savePlan / deletePlan / retryPlansHydrate) that mutate
 *     the cache on success — no full re-hydrate after a save, just a
 *     targeted `cache.set(planId, returnedPlan)`. The backend's save and
 *     list endpoints return the same shape so the cache schema stays
 *     uniform regardless of which method populated it.
 *   - A small pub/sub so UI components can re-render on hydrate state
 *     changes (success ↔ error) from a different tab/component.
 *
 * The save endpoint is versioned and append-only (every save creates a
 * new PLAN_VERSION); from the cache's perspective that's invisible — it
 * always holds the latest version per plan.
 */

import { authAxios } from '../utils/session_utils';
import { createCacheHydration } from '../utils/cache-hydration';

const API_BASE = process.env.REACT_APP_BACK_END_API;

// ─── Types ───────────────────────────────────────────────────────────────
//
// Mirror the response shape returned by the backend (`save.ts` + `list.ts`).
// Server-assigned columns (id / createdAt / savedAt) are always strings or
// numbers; nullable columns stay nullable.

/**
 * The three allowed PLAN.status values, mirrored on the backend.
 * Kept as a literal type so the picklist UI can't pass typo'd
 * values and so the API rejection path stays unreachable.
 *
 * Declared at the top of the types section because SavePlanRequest
 * references it.
 */
export const PLAN_STATUSES = ['Working', 'Hold', 'Order'] as const;
export type PlanStatus = typeof PLAN_STATUSES[number];

export interface PlanDeal {
  id: number;
  program: string;
  day: string | null;
  quarter: string;            // "Q1".."Q4"
  year: number;
  unitLength: number | null;
  demo: string | null;
  totalUnits: number | null;
  cpm: number | null;
  /**
   * Frozen v1 CPM — the reference value the per-row `± %` adjustment
   * input renders against, and what the new "Original CPM" column
   * displays. Carried forward unchanged across versions. Stamped to
   * current cpm on first save if the client omits it.
   */
  originalCpm: number | null;
  rate: number | null;
  totalDollars: number | null;
  impressions_000: number | null;
  cpmManuallyEdited: boolean;
  account: string | null;
  brand: string | null;
  sourceSfDealKey: string | null;
  /**
   * Salesforce Programs__c.ID captured at row-creation time. Used by
   * the push-to-SF flow to populate Deal__c.Program__c without a lookup
   * at push time. Null for legacy PLAN_DEAL rows created before this
   * column existed — those plans can't be pushed to SF until the user
   * re-picks the program via a repair action.
   */
  programId: string | null;
  /**
   * Secondary demo impressions (000s) for this row, keyed by demo name.
   * Empty object when the row has no secondary demos or predates the
   * PLAN_DEAL.SECONDARY_IMPRESSIONS_000 column.
   *
   * These are RESOLVED values (the user's typed figure if there was one,
   * otherwise the estimate's), the same contract as `impressions_000`
   * for the primary demo — a saved plan shouldn't move when a new
   * estimate version is uploaded.
   */
  secondaryImpressions000: Record<string, number>;
}

export interface PlanVersion {
  id: number;
  versionNo: number;
  estimateVersionId: number | null;
  /**
   * EST_VERSION.ID that supplied this plan's LATE QUARTER — 4Q'27 on a 26/27
   * plan, which the bound estimate file predates.
   *
   * Null means not pinned, which is the normal state for a plan that has
   * never put a row in that quarter: it re-resolves "newest version that has
   * the quarter" on every load, so a corrected 4Q file reaches it for free.
   * Once the plan saves rows there the id is stamped and its 4Q numbers stop
   * moving — same guarantee `estimateVersionId` gives every other quarter.
   */
  overlapEstimateVersionId: number | null;
  agency: string | null;
  advertiser: string | null;
  brand: string | null;
  demo: string | null;
  ae: string | null;
  buyer: string | null;
  marketplace: string | null;
  equiv: string | null;
  dealName: string | null;
  year: string | null;
  savedAt: string;
  savedBy: string | null;
  /**
   * Salesforce lookup IDs snapshotted at save time. Display strings
   * stay in `agency` / `advertiser` / `brand` / `buyer` above (so
   * legacy rows still render); these IDs are what the eventual SF
   * upload flow needs. All four are nullable — plans saved before
   * the lookup migration carry only the name columns.
   */
  advertiserId: string | null;
  brandId: string | null;
  contactId: string | null;
  accountId: string | null;
  /**
   * Computed-formula plan name —
   *   `{agency}-{brand}-{advertiser}-{demo}-{year}`
   * Mirrors what the SF `Sales_Plan_Name__c` workflow formula
   * produces, so we never have to write to that SF field; the
   * workflow does. Used as the uniqueness key in the Create Plan
   * modal (the legacy `dealName` / nickname can dupe across plans).
   * Nullable for pre-migration rows that haven't been backfilled.
   */
  salesPlanName: string | null;
  /**
   * Count of extra quarter tabs the user appended past the marketplace's
   * default pattern via "+ Add Quarter" in the MPB UI. 0 means "use
   * marketplace default exactly". Backend defaults the column to 0, so
   * pre-migration versions surface as 0 here without any backfill.
   */
  extraQuartersCount: number;
  /**
   * Plan-level default unit length in seconds (e.g. "30"). Set on
   * Create Plan via the "Unit Length for Plan" picker, editable later
   * via Plan → Details. New rows (+ Add Show / + Add Row) prefill
   * their unit length from this value so users don't have to pick per
   * row. Null = no default (rows start with blank length, legacy
   * behavior for plans that predate this column).
   */
  defaultUnitLength: string | null;
  /**
   * Secondary demos selected on this plan version, in display order.
   * Empty array for versions saved before PLAN_VERSION.SECONDARY_DEMOS
   * existed — those plans showed no secondary columns anyway.
   */
  secondaryDemos: string[];
  /**
   * Two-tier versioning taxonomy — see PlanVersionMeta below and the
   * key_version migration on the backend. isKey marks contractual +
   * user-named milestones; keyType discriminates; name is user-supplied
   * for named + export types.
   */
  isKey: boolean;
  keyType: PlanVersionKeyType | null;
  name: string | null;
  /**
   * Parent-milestone version_no. For key rows this equals `versionNo`;
   * for revisions it's the version_no of the nearest is_key row at or
   * before this row. Powers the header chip's parent-milestone label
   * so a plan on Revision 3 of v1 shows "v1" instead of "v4".
   */
  keyVersionNo: number;
  /**
   * 1-based milestone ordering within the plan. Powers the display
   * label so raw `versionNo = 5` (which happens to be the 2nd milestone)
   * shows as "v2". Revisions inherit their parent milestone's index.
   * Under the hood `versionNo` remains monotonic; this is display-only.
   */
  milestoneIndex: number;
  deals: PlanDeal[];
}

export interface SavedPlan {
  id: number;
  name: string;
  seasonPrimaryYear: number;
  sourcePlanName: string | null;
  sourcePlanSfId: string | null;
  status: string;
  createdAt: string;
  createdBy: string | null;
  /**
   * Push-to-Salesforce state. `pushedToSfAt` is null until the plan has
   * been pushed to SF via the "Confirm Order → Push" flow. Once set,
   * the plan is LOCKED — status is terminal-one-way (Order), the Save
   * button hides, Add Row / Add Show hide, cells become read-only.
   * `sfSalesPlanId` carries the returned Salesforce Sales_Plan__c.ID.
   */
  pushedToSfAt: string | null;
  sfSalesPlanId: string | null;
  /**
   * Latest PLAN_VERSION for this plan with its deals. Null in the edge
   * case where a PLAN row exists with no versions yet (shouldn't happen
   * post-save but the backend renders it safely).
   */
  latestVersion: PlanVersion | null;
}

/**
 * Shape sent to POST /snowflake/plans. `planId` is null on the very
 * first save of a plan, then carries forward as the cache assigns it.
 * `deals` omits the server-assigned `id` field.
 */
export interface SavePlanRequest {
  planId: number | null;
  name: string;
  seasonPrimaryYear: number;
  sourcePlanName?: string | null;
  sourcePlanSfId?: string | null;
  /** Defaults to 'Working' server-side when omitted. */
  status?: PlanStatus;
  estimateVersionId?: number | null;
  /**
   * Pinned source of the plan's late quarter. Send only once the plan has
   * rows in that quarter; carry the same value forward on every later save.
   * See PlanVersion.overlapEstimateVersionId.
   */
  overlapEstimateVersionId?: number | null;
  /**
   * Assert that this save touches ONLY the plan's late quarter. Send it when
   * the plan is a pushed Order and the user is NOT revising — the one state
   * where a single quarter is unlocked and the rest is frozen.
   *
   * The backend verifies the assertion against the stored deals and returns
   * 409 PUSHED_PLAN_LOCKED_OUTSIDE_LATE_QUARTER if anything else moved. Omit
   * it everywhere else, INCLUDING while revising an order: omitting means "no
   * assertion", which leaves the pre-feature behaviour untouched.
   */
  lateQuarterOnly?: boolean;
  /**
   * The plan version this grid was built from. The backend compares it to the
   * current MAX(version_no) and returns 409 PLAN_VERSION_CONFLICT if they
   * differ — i.e. somebody else saved while this user was editing, and writing
   * these deals would bury their work (a save sends a COMPLETE set of rows,
   * so it replaces rather than merges).
   *
   * Must be the version the grid was BUILT FROM, captured at load time — not
   * whatever the cache currently holds. The cache is refreshed on tab focus,
   * so reading it at save time would report a version the user has never seen
   * and the check would pass in exactly the case it exists to catch.
   *
   * Omit for a brand-new plan and for a save on top of a version pulled from
   * the history picker (a deliberate revert). Omitting means "no assertion".
   */
  baseVersionNo?: number | null;
  agency?: string | null;
  advertiser?: string | null;
  brand?: string | null;
  demo?: string | null;
  ae?: string | null;
  buyer?: string | null;
  marketplace?: string | null;
  equiv?: string | null;
  dealName?: string | null;
  year?: string | null;
  // Salesforce lookup IDs from the Create Plan modal's typeaheads.
  // Optional + nullable for backward compat (legacy paths that don't
  // know about lookups skip them; backend tolerates nulls).
  advertiserId?: string | null;
  brandId?: string | null;
  contactId?: string | null;
  accountId?: string | null;
  /** Computed-formula plan name; see PlanVersion.salesPlanName. */
  salesPlanName?: string | null;
  /**
   * Count of extra quarter tabs the user has appended past the
   * marketplace's default pattern via "+ Add Quarter" in the MPB UI.
   * 0 (or omitted) means "use marketplace default exactly". Persists
   * to PLAN_VERSION.EXTRA_QUARTERS_COUNT.
   */
  extraQuartersCount?: number | null;
  /**
   * Plan-level default unit length in seconds. Persisted to
   * PLAN_VERSION.DEFAULT_UNIT_LENGTH. See PlanVersion.defaultUnitLength
   * for how it's consumed. Omitted / null on save means "no default
   * set" (legacy behavior — new rows start blank).
   */
  defaultUnitLength?: string | null;
  /**
   * Secondary demos to persist with this version, in display order.
   * Omitted / empty stores NULL. See PlanVersion.secondaryDemos.
   */
  secondaryDemos?: string[] | null;
  deals: Array<Omit<PlanDeal, 'id'>>;
}

// ─── Cache ───────────────────────────────────────────────────────────────

// Lifecycle (hydrate / isHydrated / retry / onChange / error tracking) is
// owned by the shared `createCacheHydration` helper — see
// utils/cache-hydration.ts. This module owns just the Map itself.

const planCache = new Map<number, SavedPlan>();

const hydration = createCacheHydration<SavedPlan>({
  resourceName: 'saved plans',
  fetch: async () => {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/plans`,
    });
    return response?.data?.plans ?? [];
  },
  apply: (plans) => {
    planCache.clear();
    plans.forEach(p => planCache.set(p.id, p));
  },
});

/**
 * Hydrate the cache from /snowflake/plans. Safe to call multiple times —
 * shares one in-flight fetch. Called once from MediaPlanBuilder mount.
 */
export const hydratePlans = hydration.hydrate;

export const isPlansHydrated = hydration.isHydrated;

export const getPlansHydrateError = hydration.getError;

/**
 * Force a re-fetch of the plans list from Snowflake, replacing the cache.
 * Called after operations that create a new plan_version out-of-band
 * (post-order export snapshots, etc.) so the header chip / picker / any
 * dependent memo pulls the fresh milestoneIndex + keyVersionNo without
 * a full page reload.
 */
export async function refreshPlansCache(): Promise<void> {
  hydration.reset();
  await hydration.hydrate();
}

/**
 * Overlay a historical version's deals + versionNo onto the plan cache
 * WITHOUT persisting anything. Used when the user picks a past version
 * from the picker — the goal is to render that version in the live grid
 * (so they can view + optionally edit + save on top of it) rather than
 * a read-only snapshot modal.
 *
 * This mutates in memory only. The next `refreshPlansCache()` call
 * (e.g. after save success) restores the real latest from Snowflake.
 * Callers should bump their planCacheVersion state so React memos
 * re-read the mutated cache.
 *
 * `deals` must already be in the SavedPlan shape (PlanDeal[]) — the
 * caller is responsible for that transform (planDealsToDeals lives
 * in MediaPlanBuilder and it goes the other direction).
 */
export function overlayHistoricalVersion(
  planId: number,
  historical: {
    versionNo: number;
    savedAt: string;
    savedBy: string | null;
    deals: PlanDeal[];
    // Required — must be assigned directly (not via `??`) because
    // null is the CORRECT value for working revisions and would be
    // clobbered by a nullish fallback to the previous latest's
    // keyType/name. Callers pass the exact PlanVersion fields.
    isKey: boolean;
    keyType: PlanVersionKeyType | null;
    name: string | null;
    milestoneIndex: number;
    keyVersionNo: number;
  },
): void {
  const cached = planCache.get(planId);
  if (!cached || !cached.latestVersion) return;
  // Replace deal payload + version identity on the cache entry in place.
  // Rest of latestVersion (agency, advertiser, buyer, etc.) stays as-is —
  // plan metadata doesn't diverge across working revisions in practice,
  // and if it did the user can re-edit via Edit Plan Details.
  cached.latestVersion = {
    ...cached.latestVersion,
    versionNo: historical.versionNo,
    savedAt: historical.savedAt,
    savedBy: historical.savedBy,
    deals: historical.deals,
    // Direct assignment (NOT `??`) so null keyType / null name on
    // working revisions actually overrides the previous latest's
    // named/exported values. `??` here caused the header chip to
    // keep saying "Upfront V2" when the user picked v1.1.
    isKey:          historical.isKey,
    keyType:        historical.keyType,
    name:           historical.name,
    milestoneIndex: historical.milestoneIndex,
    keyVersionNo:   historical.keyVersionNo,
  };
  planCache.set(planId, cached);
}

/**
 * Retry the initial hydrate after a failure. Resets state and re-fetches.
 * Safe no-op if already in flight or succeeded.
 */
export const retryPlansHydrate = hydration.retry;

export const onPlansHydrateChange = hydration.onChange;

// ─── Read helpers (sync, off the cache) ──────────────────────────────────

/** All saved plans, oldest-first by id. */
export function listSavedPlans(): SavedPlan[] {
  const arr: SavedPlan[] = [];
  planCache.forEach(p => arr.push(p));
  return arr.sort((a, b) => a.id - b.id);
}

/** Lookup by Snowflake PLAN.id. */
export function getSavedPlanById(id: number): SavedPlan | null {
  return planCache.get(id) ?? null;
}

/**
 * Lookup by plan display name. Returns the first match if multiple plans
 * share a name.
 *
 * IMPORTANT — this match is case-sensitive on purpose. Plan names round-
 * trip through SF (where they are stored as the user typed them) and
 * Snowflake (which preserves the SF capitalisation). Loosening to a
 * case-insensitive compare would create false "saved plan exists"
 * matches between SF templates and Snowflake records that look
 * identical to humans but are actually distinct rows — and we'd start
 * showing one plan's data under another plan's name. Don't change this
 * to .toLowerCase() without auditing the load/save flow first.
 */
export function getSavedPlanByName(name: string): SavedPlan | null {
  let found: SavedPlan | null = null;
  planCache.forEach(p => {
    if (!found && p.name === name) found = p;
  });
  return found;
}

/**
 * Lookup by source SF SALES_PLAN.ID. Used during plan resolution: when
 * the user picks a plan from the dropdown, we first try this lookup
 * (saved Snowflake plan) before falling back to SF source deals.
 */
export function getSavedPlanBySfId(sfId: string): SavedPlan | null {
  let found: SavedPlan | null = null;
  planCache.forEach(p => {
    if (!found && p.sourcePlanSfId === sfId) found = p;
  });
  return found;
}

/** Set of all source SF plan IDs that have a Snowflake record. Useful
 *  for client-side de-dup; the server-side filter also enforces this. */
export function getSavedSourceSfIds(): Set<string> {
  const set = new Set<string>();
  planCache.forEach(p => {
    if (p.sourcePlanSfId) set.add(p.sourcePlanSfId);
  });
  return set;
}

/**
 * Defensive accessor for a saved plan's deals. Handles the edge case
 * where a PLAN row exists with no PLAN_VERSION yet (the list endpoint
 * renders `latestVersion: null` in that case) — callers that just want
 * "give me this plan's deals as an array" can use this without a
 * separate null check.
 *
 * Returns an empty array for:
 *   - plan not found in cache
 *   - plan exists but has no versions yet
 *   - plan exists, has a version, but the version has zero deals
 */
export function getSavedPlanDeals(planId: number): PlanDeal[] {
  const plan = planCache.get(planId);
  return plan?.latestVersion?.deals ?? [];
}

/**
 * Defensive accessor for a saved plan's latest version metadata. Returns
 * null for plans with no version yet — callers that need to read fields
 * like `estimateVersionId` should narrow with this helper rather than
 * `plan.latestVersion!` to keep type-safety.
 */
export function getSavedPlanLatestVersion(planId: number): PlanVersion | null {
  return planCache.get(planId)?.latestVersion ?? null;
}

// ─── Write helpers (async, mutate cache on success) ──────────────────────

/**
 * Persist a plan version to Snowflake. Versioned + append-only: every
 * call creates a new PLAN_VERSION with `version_no = max(existing) + 1`
 * and all deals re-inserted under it. The PLAN row itself is created
 * on first save (when `planId` is null) and re-used thereafter.
 *
 * On success the backend returns the full plan (PLAN + latest version
 * + deals); we Map.set it into the cache — no follow-up GET round-trip.
 */
export async function savePlan(
  request: SavePlanRequest,
  savedBy?: string,
): Promise<{ ok: true; plan: SavedPlan } | { ok: false; error: string; code?: string }> {
  try {
    const response = await authAxios({
      method: 'POST',
      url: `${API_BASE}/snowflake/plans`,
      data: { plan: request, savedBy: savedBy ?? null },
    });
    const saved: SavedPlan | undefined = response?.data?.plan;
    if (!saved?.id) {
      return { ok: false, error: 'Backend did not return the saved plan.' };
    }
    planCache.set(saved.id, saved);
    return { ok: true, plan: saved };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to save plan.';
    // `code` surfaced so callers can branch on the failure KIND rather than
    // string-matching the message. PLAN_VERSION_CONFLICT in particular has to
    // be handled differently from a generic failure: the user's edits stay on
    // screen and they are told someone else saved, rather than being shown a
    // toast that reads like the save is retryable.
    return { ok: false, error: msg, code: payload?.code };
  }
}

/**
 * Push a saved plan to Salesforce (Training sandbox on staging).
 *
 * Terminal one-way: on success the backend flips PLAN.status to 'Order',
 * stamps PLAN.pushed_to_sf_at + sf_sales_plan_id, and returns the
 * refreshed plan. The frontend should then render the plan as locked
 * (Save hidden, Add Row/Show hidden, cells read-only).
 *
 * Error shapes surfaced from the backend:
 *   - 400 MISSING_FIELDS: plan lacks required metadata (advertiser/brand/…)
 *   - 400 MISSING_PROGRAM_IDS: some PLAN_DEAL rows have no PROGRAM_ID
 *     (repair by re-picking the program on those rows before retrying)
 *   - 404: plan not found
 *   - 409 ALREADY_PUSHED: plan already has PUSHED_TO_SF_AT — cannot repush
 *   - 422 SF_REJECTED: Salesforce rejected the payload (field permission,
 *     validation rule, etc.); `sfDetail` holds the SF error body
 *   - 500: OAuth / network / unexpected
 *
 * Returns discriminated union so callers can render specific messages
 * for each failure without stringly-typed error parsing.
 */
export async function pushPlanToSf(
  planId: number,
): Promise<
  | { ok: true; plan: SavedPlan; sfSalesPlanId: string }
  | {
      ok: false;
      error: string;
      code?: string;
      sfDetail?: unknown;
      affectedPrograms?: string[];
      /**
       * The exact JSON body the backend sent to Salesforce. Populated
       * on SF_REJECTED failures. Useful for the modal's "copy payload"
       * affordance so we can eyeball what field SF choked on.
       */
      requestBody?: unknown;
    }
> {
  try {
    const response = await authAxios({
      method: 'POST',
      url: `${API_BASE}/snowflake/plans/${planId}/push-to-sf`,
    });
    const plan: SavedPlan | undefined = response?.data?.plan;
    const sfSalesPlanId: string | undefined = response?.data?.sfSalesPlanId;
    if (!plan?.id || !sfSalesPlanId) {
      return { ok: false, error: 'Backend did not return the pushed plan.' };
    }
    planCache.set(plan.id, plan);
    return { ok: true, plan, sfSalesPlanId };
  } catch (err: any) {
    const payload = err?.response?.data;
    return {
      ok: false,
      error: payload?.error || err?.message || 'Failed to push plan to Salesforce.',
      code: payload?.code,
      sfDetail: payload?.sfDetail,
      affectedPrograms: payload?.affectedPrograms,
      requestBody: payload?.requestBody,
    };
  }
}

/**
 * Status-only update for a saved plan. Patches PLAN.status on the
 * server (no new PLAN_VERSION — status is metadata, not a content
 * change) and mirrors the change into the local cache on success so
 * the UI rerenders with the new value immediately.
 *
 * Use this instead of `savePlan()` when only the status pill changed:
 * saves a network round-trip and avoids the version-bump churn of
 * the regular save endpoint.
 */
export async function updatePlanStatus(
  planId: number,
  status: PlanStatus,
  updatedBy?: string,
): Promise<{ ok: true; status: PlanStatus } | { ok: false; error: string }> {
  try {
    await authAxios({
      method: 'PATCH',
      url: `${API_BASE}/snowflake/plans/${planId}/status`,
      data: { status, updatedBy: updatedBy ?? null },
    });
    const cached = planCache.get(planId);
    if (cached) {
      cached.status = status;
      planCache.set(planId, cached);
    }
    return { ok: true, status };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to update status.';
    return { ok: false, error: msg };
  }
}

// ─── Plan version history (Snowflake-backed, lazy-fetched) ──────────────
//
// The cache only ever holds the *latest* version per plan — anything else
// would balloon memory on a per-plan basis and isn't needed for the
// table render. The two helpers below talk directly to the version
// endpoints when the user opens the version-history popup / picks a
// specific historical version. No caching: history opens are rare, the
// list response is small, and the data is allowed to refetch on each
// open so the popup always reflects the latest save state.

/**
 * Two-tier version taxonomy — matches PLAN_VERSION.key_type enum on the
 * backend. See services/snowflake/plans/name-version.ts and the
 * key_version migration SQL for the full context.
 */
export type PlanVersionKeyType =
  | 'initial'
  | 'order'
  | 'order_update'
  | 'named'
  | 'export'
  /**
   * A save that touched ONLY the plan's late quarter (4Q'27 on a 26/27 plan)
   * while the plan is a pushed Order. Not a milestone — `is_key` stays false
   * and it is excluded from every `key_type IN (...)` milestone list.
   *
   * It exists to be excluded from ONE inference: `hasInflightPostOrderRevision`
   * treats any non-order save landing after the order as "someone is
   * mid-revision" and unlocks the whole plan. A late-quarter save must not
   * trigger that — the rest of the plan stays locked. The backend also uses
   * this tag to scope what the save is permitted to write.
   */
  | 'late_quarter';

/**
 * Lightweight metadata for one historical version of a plan. Mirrors
 * the response shape from GET /snowflake/plans/:id/versions (see
 * services/snowflake/plans/list-versions.ts on the backend).
 *
 * Lifecycle model (rewritten):
 *   • Every row belongs to a working lifecycle keyed by milestoneIndex
 *     (1, 2, 3, ...). Every row in the same lifecycle shares one.
 *   • The FIRST row of each lifecycle is its OPENER — reads as "v{N}",
 *     revisionNumber=0. For v1 the opener has key_type='initial'; for
 *     v2+ it's a regular save that happens to be the first after the
 *     previous lifecycle's closure.
 *   • Working revisions (2nd row onward within a lifecycle) get
 *     revisionNumber 1, 2, 3, ... — read as "v{N}.1", "v{N}.2".
 *   • CLOSURES end a lifecycle. is_key=TRUE with key_type in
 *     ('named','export','order','order_update'). revisionNumber=0.
 *     Their identity is the name (or "Live Order" for orders).
 *   • keyVersionNo — version_no of the row's lifecycle opener. Every
 *     row (including closures) points back to its opener.
 */
export interface PlanVersionMeta {
  versionNo: number;
  estimateVersionId: number | null;
  savedAt: string;
  savedBy: string | null;
  dealCount: number;
  isKey: boolean;
  keyType: PlanVersionKeyType | null;
  name: string | null;
  revisionNumber: number;
  keyVersionNo: number | null;
  /**
   * 1-based milestone ordering within the plan. Same field as on
   * `PlanVersion` (see there for details). Populated by the backend
   * list-versions endpoint.
   */
  milestoneIndex: number;
}

/**
 * Fetch every saved version of a plan (newest first, no deal payload).
 * Powers the version-history popup anchored to the chip in the MPB
 * header. Soft-deleted plans 404; we surface a generic error string
 * either way because the UI just renders it inline.
 */
export async function listPlanVersions(
  planId: number,
): Promise<{ ok: true; versions: PlanVersionMeta[] } | { ok: false; error: string }> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/plans/${planId}/versions`,
    });
    const versions: PlanVersionMeta[] = response?.data?.versions ?? [];
    return { ok: true, versions };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to load version history.';
    return { ok: false, error: msg };
  }
}

/**
 * Fetch the full deal payload for one specific historical version of a
 * plan. Powers the read-only snapshot viewer modal — clicking a row in
 * the version-history popup invokes this. Returns the same
 * `PlanVersion` shape used by `SavedPlan.latestVersion`.
 */
export async function getPlanVersion(
  planId: number,
  versionNo: number,
): Promise<{ ok: true; version: PlanVersion } | { ok: false; error: string }> {
  try {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/plans/${planId}/versions/${versionNo}`,
    });
    const version: PlanVersion | undefined = response?.data?.version;
    if (!version) {
      return { ok: false, error: 'Backend did not return the requested version.' };
    }
    return { ok: true, version };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to load version.';
    return { ok: false, error: msg };
  }
}

/**
 * Promote a specific version to a "named" key version (or rename an
 * already-named one). Contractual key_types on the backend keep their
 * type; only `name` changes. See name-version.ts on the backend for
 * the precise semantics.
 */
export async function namePlanVersion(
  planId: number,
  versionNo: number,
  name: string,
  // Optional key_type override — 'order_update' is used by the
  // post-order revision-finalize flow. Everything else falls back
  // to backend default ('named'). Callers that don't specify get
  // the historical behavior.
  keyType?: 'named' | 'order_update',
): Promise<{ ok: true; version: { versionNo: number; isKey: true; keyType: PlanVersionKeyType; name: string } } | { ok: false; error: string }> {
  try {
    const response = await authAxios({
      method: 'POST',
      url: `${API_BASE}/snowflake/plans/${planId}/versions/${versionNo}/name`,
      data: keyType ? { name, keyType } : { name },
    });
    return { ok: true, version: response.data.version };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to name version.';
    return { ok: false, error: msg };
  }
}

/**
 * Create a snapshot version by copying the source (or latest) version's
 * deals into a new PLAN_VERSION row tagged key_type='export'. Works on
 * any plan status. Called when the user picks "Save & Export" from the
 * export-as-key modal — the modal itself only appears when the current
 * version isn't already a key version, so re-exports of an already-key
 * version skip this endpoint entirely.
 */
export async function snapshotVersionForExport(
  planId: number,
  name: string,
  sourceVersionNo?: number,
  // Optional key_type override — 'order_update' is passed when the
  // export IS a post-order revision finalize. Default is 'export'.
  keyType?: 'export' | 'order_update',
): Promise<{ ok: true; version: { versionNo: number; isKey: true; keyType: 'export' | 'order_update'; name: string } } | { ok: false; error: string }> {
  try {
    const body: Record<string, unknown> = { name };
    if (sourceVersionNo != null) body.sourceVersionNo = sourceVersionNo;
    if (keyType) body.keyType = keyType;
    const response = await authAxios({
      method: 'POST',
      url: `${API_BASE}/snowflake/plans/${planId}/versions/export`,
      data: body,
    });
    return { ok: true, version: response.data.version };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to save export snapshot.';
    return { ok: false, error: msg };
  }
}

/**
 * Soft-delete a plan. Removes it from the local cache on success — the
 * backend keeps the row + history with `deleted_at` set, so an admin
 * can restore via Snowsight if needed.
 */
export async function deletePlan(
  planId: number,
  deletedBy?: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    // Pass deletedBy as a query string rather than a request body —
    // standard HTTP semantics for DELETE don't include a body, and some
    // proxies / CDNs drop it. Backend reads it from req.query.
    const url = deletedBy
      ? `${API_BASE}/snowflake/plans/${planId}?deletedBy=${encodeURIComponent(deletedBy)}`
      : `${API_BASE}/snowflake/plans/${planId}`;
    await authAxios({ method: 'DELETE', url });
    planCache.delete(planId);
    return { ok: true };
  } catch (err: any) {
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to delete plan.';
    return { ok: false, error: msg };
  }
}
